// The pages that list games: the home list (with its tag cards), the tags
// index (static, and with its cards), a tag's page, a company's page and the
// search page, each as rows from rows.ts under a pager. Their head tags are
// meta.ts's. Nothing here touches the DOM.

import type {
  ActiveRow,
  AppsResponse,
  CompanyResponse,
  MoversResponse,
  SearchResponse,
  TagResponse
} from "../../../shared/api.ts";
import { COMPANY_ROLES, COMPANY_ROLE_LIST } from "../../../shared/companies.ts";
import { HOME_TAGS, TAGS_PATH, companyPath, tagPagePath } from "../../../shared/site.ts";
import { hour, number } from "../figures.ts";
import { type PagedList, backLinkHtml, pageLabel, pager, plural, sectionHead, tabs } from "../markup.ts";
import { type CompanyPageRef, SEARCH_PATH, type SearchQuery, listFound, searchHref, searchPath } from "../routes.ts";
import { appRow, tagCard, tagFigures } from "../rows.ts";
import {
  TAG_PAGES,
  TAG_PAGE_NAMES,
  type TagPageName,
  type TagPageRef,
  type TagPageValue
} from "./tag-pages.ts";
import { escapeHtml } from "../web.ts";

// How many games each of page 0's tag cards shows (see HOME_TAGS). It asks
// for TAGS_PREVIEW_GAMES, as /tags does (one cached response for both), so a
// card can skip games an earlier card showed.
const HOME_TAG_GAMES = 3;

// Which hour a ranking is for (its as_of, written by `format`), since it
// trails the hourly counts: it's published once an hour is complete. "" for
// none yet.
const rankedAsOf = (asOf: number | null, format: (t: number) => string): string =>
  asOf == null ? "" : ` Ranked as of ${format(asOf)}.`;

// The top of a list page. When the ranking is from goes with the ranking
// (listBody's "Most played" head).
export const listIntro = (): string => `
  <h1 class="list-title">Steam player counts</h1>
  <p class="muted list-sub">Concurrent players on Steam, recorded hourly.</p>`;

// In a box of its own, which its rows' layout queries for the list's width
// (see "List widths" in style.css), under column labels (listHead's) shown
// where the list is wide, `figures` naming what its rows show by the count.
const appList = (rows: string[], figures: string): string =>
  `<div class="list-box"><ol class="panel app-list">${listHead(figures)}${rows.join("")}</ol></div>`;

// A list's column labels, for appList, `figures` naming the figures
// beside each count: hidden from screen readers, whose rows say what each
// figure is.
const listHead = (figures: string): string => `
  <li class="list-head" aria-hidden="true">
    <span class="rank">#</span><span></span><span>Game</span>
    <span class="trend">7 days</span><span class="count">Players</span><span>${figures}</span>
  </li>`;

// The figures appRow shows by default, as listHead labels them.
const ROW_FIGURES = "24h · Peak";

// What a tag page's rows show beside the count (rows.ts's tagFigures), as
// listHead labels it.
const TAG_FIGURE_LABELS: Readonly<Record<TagPageName, string>> = {
  trend: "24h",
  rank: "Places · 24h",
  peak_share: "Of peak",
  update_bump: "After update",
  sale_bump: "During sale",
  on_sale: "Price",
  new_release: "Released",
  busy_hours: "Vs avg hour"
};

// ---- The tags index -----------------------------------------------------

// The tags index's heading, shared by its static page and its cards.
const tagsHead = `
  ${backLinkHtml()}
  <header class="tags-head">
    <h1>Tags</h1>
    <p class="muted">Games grouped by how their player counts are moving and
      when they're played, read from the hourly counts, and the new releases
      most played.</p>
  </header>`;

// Tags with one page that sit next to another such tag in TAG_PAGES, paired
// off in order: the tags index puts each pair side by side, so neither
// section leaves half its row empty. A one-page tag left over gets a row.
const PAIRED_TAGS = (() => {
  const paired = new Set<string>();
  let waiting: string | null = null;
  for (const [tag, { values }] of Object.entries(TAG_PAGES)) {
    if (Object.keys(values).length !== 1) waiting = null;
    else if (!waiting) waiting = tag;
    else {
      paired.add(waiting).add(tag);
      waiting = null;
    }
  }
  return paired;
})();

// The tags index's sections: each tag, by name, over `list(tag, values)`.
const tagSections = (list: (tag: TagPageName, values: Record<string, TagPageValue>) => string): string => `
  <div class="tag-groups">${TAG_PAGE_NAMES
    .map((tag) => [tag, TAG_PAGES[tag]] as const)
    .map(
      ([tag, { name, values }]) => `
    <section class="tag-group${PAIRED_TAGS.has(tag) ? " single" : ""}">
      <h2>${name}</h2>
      ${list(tag, values)}
    </section>`
    )
    .join("")}
  </div>`;

// The tags index's content: every tag page, by tag, each with what it lists.
// Static, so build.ts writes it into tags.html, served at /tags as a free
// asset request, as about.html is.
export const tagsPage = `
  ${tagsHead}
  ${tagSections(
    (tag, values) => `<ul class="tag-links">${Object.entries(values)
      .map(
        ([value, { title, text }]) => `<li>
          <a href="${tagPagePath(tag, value)}" data-nav>${title}</a>
          <span class="muted">${text}</span></li>`
      )
      .join("")}</ul>`
  )}`;

// Page `page` of a paged list at `path`, from its response `list`: its rows
// (each game by `row`, given the game alone) and the pager; for a page past
// the end, says so; for an empty list, `empty`.
// Each list has column labels and a week's trend for each game where it's
// wide, `figures` labelling what its rows show beside the count. With more
// than one page, the pager says which of the list's games these are, unless
// `count` is false (a page that already says how many there are).
function pagedBody<R>(
  page: number,
  list: PagedList<R>,
  path: string,
  row: (game: R) => string,
  empty: string,
  { figures = ROW_FIGURES, count = true }: { figures?: string; count?: boolean } = {}
): string {
  if (list.apps.length) {
    const first = page * list.per_page + 1;
    const last = first + list.apps.length - 1;
    const shown = count && list.pages > 1
      ? `<p class="muted pager-shown">Showing ${number.format(first)}–${number.format(
          last)} of ${number.format(list.total)}</p>`
      : "";
    return (
      appList(list.apps.map((game) => row(game)), figures) +
      pager(page, list.pages, path) +
      shown
    );
  }
  if (listFound(list)) return `<div class="panel status">${empty}</div>`;

  return `<div class="panel status">
      There's no page ${pageLabel(page)}. <a href="${path}" data-nav>Back to page 1</a>
    </div>`;
}

// ---- The home list ----------------------------------------------------

// Page 0's tags area: a card for each of HOME_TAGS from /api/movers's
// response (`movers`). Nothing when there's no response or no tag has
// games, so the page is just the ranking, as on later pages.
// Each card prefers games no earlier card showed (a top riser is often a
// top climber too), filling up with ones that were when the tag has too few
// others, so a card is never shorter than the tag's list allows; one with
// `keepTop` (HOME_TAGS) just shows its top games. Its games keep the tag's
// order.
function tagCards(movers: MoversResponse | null | undefined): string {
  const shown = new Set<number>();
  const cards = HOME_TAGS.map(({ tag, value, keepTop }) => {
    const all = movers?.tags?.[tag]?.[value] ?? [];
    const fresh = keepTop ? all : all.filter((game) => !shown.has(game.app_id));
    const picked = new Set(
      [...fresh, ...all.filter((game) => !fresh.includes(game))].slice(0, HOME_TAG_GAMES)
    );
    const apps = all.filter((game) => picked.has(game));
    for (const game of apps) shown.add(game.app_id);
    return tagCard(tag, value, apps);
  }).join("");
  if (!cards) return "";

  return `
      <section class="home-tags">
        ${sectionHead(
        "Movers",
        "How games are moving, and what's on sale, as of the latest hour.",
        `<a class="section-more" href="${TAGS_PATH}" data-nav>All tags →</a>`
      )}
        <ol class="tag-cards swipe">${cards}</ol>
      </section>`;
}

// A list page under its intro, from its /api/apps response (`list`) and,
// on page 0, /api/movers's (`movers`; none on later pages, or when it
// failed: it's an extra, so the ranking shows without it). For a page past
// the end, or no ranking yet, says so instead.
// The ranking has its own head, saying which hour it's for.
export function listBody(page: number, list: AppsResponse, movers: MoversResponse | null | undefined): string {
  const tags = list.apps.length ? tagCards(movers) : "";
  return `
      ${tags}
      <section>
        ${sectionHead(
        "Most played",
        `By concurrent players.<span class="as-of">${rankedAsOf(list.as_of, hour)}</span>`
      )}
        ${pagedBody(page, list, "/", appRow, "No games are being tracked yet.")}
      </section>`;
}

// ---- Tags ---------------------------------------------------------------

// The tags index once its games are in: every tag page as a card of its
// top games, by tag. `tags` is shaped like /api/movers's: { tag: { value:
// games } }.
export function tagsBody(tags: MoversResponse["tags"] | null | undefined): string {
  return `
      ${tagsHead}
      ${tagSections(
      (tag, values) => `<ol class="tag-cards">${Object.keys(values)
        .map((value) => tagCard(tag, value, tags?.[tag]?.[value], true))
        .join("")}</ol>`
    )}`;
}

// A tag page (routes.ts's tagPageOf) for page `page` of its GET
// /api/tags/<tag> response, `list`; undefined while it loads. Its intro,
// with how often the tag is refreshed once the API says, tabs for the tag's
// other values, then rows like the home page's, badges and all.
export function tagPage({ tag, value, def }: TagPageRef, page: number, list: TagResponse | null | undefined): string {
  const { name, values } = TAG_PAGES[tag];
  const path = tagPagePath(tag, value);
  const valueTabs = tabs(
    name,
    Object.entries(values).map(([other, { tab }]: [string, TagPageValue]) => ({
      href: tagPagePath(tag, other),
      text: tab,
      current: other === value
    }))
  );

  const body = list
    ? pagedBody(
        page,
        list,
        path,
        (game) => appRow(game, tagFigures[tag](game), tag),
        def.empty ?? "No games have this tag right now.",
        { figures: TAG_FIGURE_LABELS[tag] }
      )
    : `<div class="panel status">Loading…</div>`;

  const every = list?.every ? ` Retagged every ${list.every}.` : "";

  return `
      ${backLinkHtml(TAGS_PATH, "All tags")}
      <h1>${def.title}</h1>
      <p class="muted">${def.text}${every}</p>
      ${valueTabs}
      ${body}`;
}

// ---- Companies ----------------------------------------------------------

// A company page's row: a game no longer tracked says so in place of its
// last count, which the page's total of players leaves out.
const companyRow = (game: ActiveRow): string =>
  game.active === false
    ? appRow({ ...game, player_count: null }, `<small class="change">no longer tracked</small>`)
    : appRow(game);

// A company page (companyRoute's `page`) for page `page` of its GET
// /api/companies response, `list`; undefined while it loads. Its name, how
// many games and players, tabs between its two roles when it has both,
// then rows like the home page's.
export function companyPage({ role, slug }: CompanyPageRef, page: number, list: CompanyResponse | null | undefined): string {
  if (!list) {
    return `${backLinkHtml()}<div class="panel status">Loading…</div>`;
  }

  const { label, verb } = COMPANY_ROLES[role];
  const path = companyPath(role, slug);

  const roleTabs = list.other_role
    ? tabs(
        "Roles",
        COMPANY_ROLE_LIST.map((r) => ({
          href: companyPath(r, slug),
          text: `${COMPANY_ROLES[r].tab} (${number.format(r === role ? list.total : list.other_role)})`,
          current: r === role
        }))
      )
    : "";

  // Its players, over the games still ranked when some aren't.
  const playing = list.ranked
    ? ` <strong>${number.format(list.players)}</strong> playing${
        list.ranked < list.total
          ? ` the ${number.format(list.ranked)} still tracked`
          : ""
      } at the latest count.${rankedAsOf(list.as_of, hour)}`
    : "";

  return `
      ${backLinkHtml()}
      <h1>${escapeHtml(list.name)}</h1>
      <p class="muted">${label} of ${number.format(list.total)} tracked
        ${plural(list.total, "game")}, most played first.${playing}</p>
      ${roleTabs}
      ${pagedBody(page, list, path, companyRow, `No tracked games ${verb} by ${escapeHtml(list.name)}.`)}`;
}

// ---- Search -------------------------------------------------------------

// The search page for `query` (searchQuery's), under `back`: its form,
// then searchResults for its /api/search response, `list` (undefined while
// it loads). app.js keeps the form while only the query changes, and
// replaces the results.
export function searchPage(query: SearchQuery, back: string, list: SearchResponse | null | undefined): string {
  const { q, tags } = query;
  const groups = TAG_PAGE_NAMES
    .map((tag) => [tag, TAG_PAGES[tag]] as const)
    .map(
      ([tag, { name, values }]) => `
        <fieldset class="filter-group">
          <legend>${name}</legend>
          ${Object.entries(values)
          .map(
            ([value, { tab, short }]: [string, TagPageValue]) => `<label title="${escapeHtml(short)}"><input
                type="checkbox" name="${tag}" value="${value}"
                ${tags[tag]?.includes(value) ? "checked" : ""}> ${tab}</label>`
          )
          .join("")}
        </fieldset>`
    )
    .join("");

  return `
    ${back}
    <h1>Search</h1>
    <p class="muted">Tracked games by name, and by tag: games with every kind
      of tag you pick, and any of the values you pick for each, most played
      first. Tags go on the ranked
      games only. <a href="${TAGS_PATH}" data-nav>What the tags mean →</a></p>
    <form class="panel search-form" action="${SEARCH_PATH}" role="search"
          aria-label="Search by name and tag">
      <div class="search-name">
        <input type="search" name="q" value="${escapeHtml(q)}" maxlength="100"
               placeholder="Any name" aria-label="Game name" enterkeyhint="search">
        <button type="submit">Search</button>
        <a href="${SEARCH_PATH}" data-nav>Clear</a>
      </div>
      <div class="filter-groups">${groups}</div>
    </form>
    <p class="visually-hidden search-status" aria-live="polite"></p>
    <section class="search-results">${searchResults(query, list)}</section>`;
}

// How many games a search found, from its /api/search response.
export const searchCount = (list: Pick<SearchResponse, "total">): string =>
  list.total ? `${number.format(list.total)} ${plural(list.total, "game")}` : "No games";

// A search's results, from its /api/search response (`list`;
// undefined while it loads), with how many there are and a pager.
export function searchResults(query: SearchQuery, list: SearchResponse | null | undefined): string {
  if (!searchPath(query, query.page)) {
    return `<div class="panel status">Type a name or pick a tag.</div>`;
  }
  if (!list) return `<div class="panel status">Searching…</div>`;

  const count = list.total ? `<p class="muted search-count">${searchCount(list)}</p>` : "";

  return (
    count +
    pagedBody(query.page, list, searchHref(query), appRow, "No tracked games match.", {
      count: false
    })
  );
}
